import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

import { addDays, addMonths, startOfDay, startOfMonth } from "../dateUtils";

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** (year, month)의 마지막 날. new Date(y, m+1, 0). */
const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
/** day를 대상 월의 말일로 clamp — 1/31에서 PageDown 시 3/3으로 넘치는 것 방지(→ 2/28). */
const clampDayToMonth = (year: number, month: number, day: number) =>
  new Date(year, month, Math.min(day, daysInMonth(year, month)));

export interface UseCalendarKeyboardParams {
  initialFocusDate: Date;
  weekStartsOn: number;
  dual: boolean;
  leftView: Date;
  rightView: Date;
  /** focus effect가 viewDate 전환 후에도 재실행되도록 dep로 받는다. */
  viewDate: Date;
  /** 포커스가 보이는 달을 벗어날 때 달을 이동(prev/next와 공용). */
  goToMonth: (target: Date, dir: 1 | -1) => void;
  onSelectDate: (date: Date) => void;
}

/**
 * 달력 day 그리드의 **키보드 탐색 + roving tabindex + 포커스 이동**을 소유하는 hook(WAI-ARIA grid).
 * ←/→ 하루, ↑/↓ 한 주, Home/End 주의 처음/끝, PageUp/Down 한 달(일자 보존 + 월말 clamp),
 * Enter/Space 선택. 포커스가 보이는 달을 벗어나면 그 달로 viewDate를 옮긴다(두 달 함께).
 */
export function useCalendarKeyboard({
  initialFocusDate,
  weekStartsOn,
  dual,
  leftView,
  rightView,
  viewDate,
  goToMonth,
  onSelectDate,
}: UseCalendarKeyboardParams) {
  const [focusedDate, setFocusedDate] = useState<Date>(() => startOfDay(initialFocusDate));
  const dayRefs = useRef(new Map<number, HTMLButtonElement>());
  const pendingFocus = useRef(false);

  // 키보드로 focusedDate가 바뀌면 해당 버튼에 실제 포커스를 옮긴다(roving tabindex).
  useEffect(() => {
    if (!pendingFocus.current) return;
    pendingFocus.current = false;
    dayRefs.current.get(startOfDay(focusedDate).getTime())?.focus();
  }, [focusedDate, viewDate]);

  const isVisible = useCallback(
    (day: Date) => isSameMonth(day, leftView) || (dual && isSameMonth(day, rightView)),
    [dual, leftView, rightView],
  );

  const moveFocus = useCallback(
    (next: Date) => {
      const day = startOfDay(next);
      pendingFocus.current = true;
      setFocusedDate(day);
      // 포커스가 보이는 달을 벗어나면 그 달이 보이도록 viewDate 이동(두 달 함께).
      if (!isVisible(day)) {
        const dir: 1 | -1 = startOfMonth(day) > startOfMonth(leftView) ? 1 : -1;
        if (dual && dir === 1) {
          goToMonth(addMonths(startOfMonth(day), -1), 1);
        } else {
          goToMonth(startOfMonth(day), dir);
        }
      }
    },
    [dual, isVisible, leftView, goToMonth],
  );

  const onGridKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const key = event.key;
      let next: Date | null = null;
      if (key === "ArrowLeft") next = addDays(focusedDate, -1);
      else if (key === "ArrowRight") next = addDays(focusedDate, 1);
      else if (key === "ArrowUp") next = addDays(focusedDate, -7);
      else if (key === "ArrowDown") next = addDays(focusedDate, 7);
      else if (key === "Home")
        next = addDays(focusedDate, -((focusedDate.getDay() - weekStartsOn + 7) % 7));
      else if (key === "End")
        next = addDays(focusedDate, 6 - ((focusedDate.getDay() - weekStartsOn + 7) % 7));
      else if (key === "PageUp") next = addMonths(focusedDate, -1);
      else if (key === "PageDown") next = addMonths(focusedDate, 1);
      else if (key === "Enter" || key === " ") {
        event.preventDefault();
        onSelectDate(focusedDate);
        return;
      } else {
        return;
      }
      event.preventDefault();
      // PageUp/Down은 addMonths가 1일로 떨어지므로 focusedDate의 일자를 보존하되, 대상 월의
      // 말일을 넘으면 clamp(예: 1/31 + PageDown → 3/3이 아니라 2/28).
      if (key === "PageUp" || key === "PageDown") {
        moveFocus(clampDayToMonth(next.getFullYear(), next.getMonth(), focusedDate.getDate()));
      } else {
        moveFocus(next);
      }
    },
    [focusedDate, moveFocus, onSelectDate, weekStartsOn],
  );

  const registerDayRef = useCallback((date: Date, el: HTMLButtonElement | null) => {
    if (el) dayRefs.current.set(startOfDay(date).getTime(), el);
    else dayRefs.current.delete(startOfDay(date).getTime());
  }, []);

  return { focusedDate, setFocusedDate, onGridKeyDown, registerDayRef };
}
